import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useSearch } from '../hooks/useSearch';
import { useArtists } from '../hooks/useArtists';
import { useExhibitions } from '../hooks/useExhibitions';
import { SearchResult, Artist, Exhibition } from '../types';
import Loading from '../components/Loading';
import ErrorDisplay from '../components/ErrorDisplay';
import { formatExhibitionName } from '../utils/formatExhibitionName';

interface SearchFilters {
  artist: string;
  year: string;
  type: string;
}

const SearchResults: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [filters, setFilters] = useState<SearchFilters>({
    artist: searchParams.get('artist') || '',
    year: searchParams.get('year') || '',
    type: searchParams.get('type') || ''
  });

  const { search, results, isSearching, error } = useSearch();
  const { artists } = useArtists({ limit: 100 });
  const { exhibitions } = useExhibitions({ limit: 50 });

  // Perform search when query changes
  useEffect(() => {
    if (searchQuery.trim().length >= 2) {
      search(searchQuery);
    }
  }, [searchQuery, search]);

  // Update URL when filters change
  useEffect(() => {
    const params = new URLSearchParams();
    if (searchQuery) params.set('q', searchQuery);
    if (filters.artist) params.set('artist', filters.artist);
    if (filters.year) params.set('year', filters.year);
    if (filters.type) params.set('type', filters.type);
    
    setSearchParams(params);
  }, [searchQuery, filters, setSearchParams]);

  // Filter results based on selected filters
  const filteredResults = useMemo(() => {
    let filtered = results;

    if (filters.artist) {
      filtered = filtered.filter(result => 
        result.artist_name?.includes(filters.artist) || result.artist_slug === filters.artist
      );
    }

    if (filters.year) {
      // This would need to be implemented based on how year data is stored in search results
      // For now, we'll skip year filtering until we have that data structure
    }

    if (filters.type) {
      filtered = filtered.filter(result => result.type === filters.type);
    }

    return filtered;
  }, [results, filters]);

  // Get unique years from artists/artworks (mock implementation)
  const availableYears = useMemo(() => {
    // This is a simplified version - in reality you'd extract years from artwork data
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear; i >= currentYear - 10; i--) {
      years.push(i.toString());
    }
    return years;
  }, []);

  const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (filterType: keyof SearchFilters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const clearFilters = () => {
    setFilters({
      artist: '',
      year: '',
      type: ''
    });
  };

  const handleResultClick = (result: SearchResult) => {
    // Scroll to top when navigating from search results
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (result.type === 'artist') {
      const targetSlug = result.artist_slug || result.id;
      // Properly encode the URL for Hebrew characters
      navigate(`/artist/${encodeURIComponent(targetSlug)}`);
    } else if (result.type === 'artwork') {
      if (result.artist_slug) {
        navigate(`/artist/${encodeURIComponent(result.artist_slug)}`);
      }
    } else if (result.type === 'exhibition') {
      navigate('/exhibition');
    }
  };

  const truncateText = (text: string, maxLength: number = 150): string => {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  const getSearchTypeLabel = (type: string): string => {
    const labels: Record<string, string> = {
      'artist': 'מציג.ה',
      'artwork': 'יצירה',
      'exhibition': 'תערוכה'
    };
    return labels[type] || type;
  };

  return (
    <div className="min-h-screen bg-gray-50" dir="rtl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Search Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-6">חיפוש באתר</h1>
          
          {/* Search Input */}
          <div className="relative mb-6">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                  <svg
                    className="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  placeholder="חיפוש טקסט..."
                  value={searchQuery}
                  onChange={handleSearchInput}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && searchQuery.trim().length >= 2) {
                      search(searchQuery.trim());
                    }
                  }}
                  className="w-full pr-10 pl-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-right"
                  dir="rtl"
                />
              </div>
              <button
                onClick={() => {
                  if (searchQuery.trim().length >= 2) {
                    search(searchQuery.trim());
                  }
                }}
                disabled={searchQuery.trim().length < 2}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                חפש
              </button>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              {/* Artist Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סינון לפי מציג.ה
                </label>
                <select
                  value={filters.artist}
                  onChange={(e) => handleFilterChange('artist', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">כל האמנים</option>
                  {artists.map((artist) => (
                    <option key={artist.id} value={artist.slug}>
                      {artist.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Year Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סינון לפי שנה
                </label>
                <select
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">כל השנים</option>
                  {availableYears.map((year) => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>

              {/* Type Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  סינון לפי סוג
                </label>
                <select
                  value={filters.type}
                  onChange={(e) => handleFilterChange('type', e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">כל הסוגים</option>
                  <option value="artist">מציג.ה</option>
                  <option value="artwork">יצירה</option>
                  <option value="exhibition">תערוכה</option>
                </select>
              </div>

              {/* Clear Filters */}
              <div className="flex items-end">
                <button
                  onClick={clearFilters}
                  className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors"
                >
                  נקה סינון
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Results Section */}
        <div>
          {/* Results Count */}
          {searchQuery.length >= 2 && !isSearching && (
            <div className="mb-4 text-gray-600">
              נמצאו {filteredResults.length} תוצאות עבור "{searchQuery}"
            </div>
          )}

          {/* Loading State */}
          {isSearching && (
            <div className="flex justify-center py-12">
              <Loading />
            </div>
          )}

          {/* Error State */}
          {error && (
            <ErrorDisplay
              error={error}
              title="שגיאה בחיפוש"
            />
          )}

          {/* No Results */}
          {searchQuery.length >= 2 && !isSearching && !error && filteredResults.length === 0 && (
            <div className="text-center py-12">
              <div className="text-gray-500 text-lg mb-2">
                לא נמצאו תוצאות
              </div>
              <div className="text-gray-400">
                נסה לשנות את מילות החיפוש או הסינון
              </div>
            </div>
          )}

          {/* Search Instructions */}
          {searchQuery.length < 2 && (
            <div className="text-center py-12 text-gray-500">
              הכנס לפחות 2 תווים כדי להתחיל לחפש
            </div>
          )}

          {/* Results Table */}
          {searchQuery.length >= 2 && !isSearching && !error && filteredResults.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200" dir="rtl">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        שם הדף/העבודה
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        תקציר טקסט
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        סוג
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        מציג.ה
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        פעולות
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredResults.map((result, index) => (
                      <tr 
                        key={`${result.type}-${result.id}-${index}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {result.image_url && (
                              <div className="flex-shrink-0 h-10 w-10 ml-4">
                                <img
                                  className="h-10 w-10 rounded-lg object-cover"
                                  src={result.image_url}
                                  alt={result.title}
                                />
                              </div>
                            )}
                            <div>
                              <div className="text-sm font-medium text-gray-900">
                                {result.title}
                              </div>
                              {result.exhibition_name && (
                                <div className="text-sm text-gray-500">
                                  {formatExhibitionName(result.exhibition_name)}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 max-w-xs">
                            {result.description ? truncateText(result.description, 150) : '—'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {getSearchTypeLabel(result.type)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {result.artist_name || '—'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <button
                            onClick={() => handleResultClick(result)}
                            className="text-blue-600 hover:text-blue-900 flex items-center gap-1"
                          >
                            עבור לדף
                            <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                            </svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SearchResults;